import { Children, isValidElement } from 'react';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import isFragment from '../../pro/lib/_util/isFragment';
import { iteratorFindIndex, iteratorSome } from '../../pro/lib/_util/iteratorUtils';
import { TabsPosition } from './enum';
import { isTabGroup } from './TabGroup';
export function toGroups(children) {
    const c = [];
    Children.forEach(children, child => {
        if (child) {
            if (isFragment(child)) {
                c.push(...toGroups(child.props.children));
            }
            else if (isTabGroup(child)) {
                c.push(child);
            }
        }
    });
    return c;
}
export function toArray(children) {
    const c = [];
    Children.forEach(children, child => {
        if (child) {
            if (isFragment(child)) {
                c.push(...toArray(child.props.children));
            }
            else if (isValidElement(child)) {
                c.push(child);
            }
        }
    });
    return c;
}
export function getDefaultActiveKeyInGroup(panelMap) {
    let activeKey;
    iteratorSome(panelMap.entries(), ([key, panel]) => {
        if (!panel.disabled) {
            activeKey = key;
            return true;
        }
        return false;
    });
    return activeKey;
}
export function getDefaultActiveKey(totalPanelsMap, groupedPanelsMap, option) {
    const { activeKey, defaultActiveKey } = option;
    if (activeKey !== undefined) {
        return activeKey;
    }
    if (defaultActiveKey !== undefined) {
        return defaultActiveKey;
    }
    const { value } = groupedPanelsMap.values().next();
    if (value) {
        const { group: { defaultActiveKey: groupDefaultActiveKey } } = value;
        if (groupDefaultActiveKey !== undefined) {
            return groupDefaultActiveKey;
        }
    }
    return getDefaultActiveKeyInGroup(totalPanelsMap);
}
export function getDefaultGroupKey(groupedPanelsMap) {
    let groupKey;
    iteratorSome(groupedPanelsMap.entries(), ([key, { panelsMap }]) => (iteratorSome(panelsMap.values(), (panel) => {
        if (!panel.disabled) {
            groupKey = key;
            return true;
        }
        return false;
    })));
    return groupKey;
}
export function getActiveKeyByGroupKey(groupedPanelsMap, key) {
    const map = groupedPanelsMap.get(key);
    if (map) {
        const { group, panelsMap, lastActiveKey } = map;
        if (lastActiveKey) {
            return lastActiveKey;
        }
        if ('defaultActiveKey' in group) {
            return group.defaultActiveKey;
        }
        return getDefaultActiveKeyInGroup(panelsMap);
    }
}
export function generateKey(key, index) {
    return String(isNil(key) ? index : key);
}
export function getActiveIndex(map, activeKey) {
    return activeKey === undefined ? -1 : iteratorFindIndex(map.keys(), key => key === activeKey);
}
export function setTransform(style, v = '') {
    style.transform = v;
    style.webkitTransform = v;
}
export function isTransformSupported(style) {
    return 'transform' in style || 'webkitTransform' in style;
}
export function setTransition(style, v = '') {
    style.transition = v;
    style.webkitTransition = v;
}
export function getTransformPropValue(v) {
    return {
        transform: v,
        WebkitTransform: v,
    };
}
export function isVertical(tabBarPosition) {
    return tabBarPosition === TabsPosition.left || tabBarPosition === TabsPosition.right;
}
export function getTransformByIndex(index, tabBarPosition) {
    const translate = isVertical(tabBarPosition) ? 'translateY' : 'translateX';
    return `${translate}(${-index * 100}%) translateZ(0)`;
}
export function getMarginStyle(index, tabBarPosition) {
    const marginDirection = isVertical(tabBarPosition) ? 'marginTop' : 'marginLeft';
    return {
        [marginDirection]: `${-index * 100}%`,
    };
}
export function getStyle(el, property) {
    return +getComputedStyle(el).getPropertyValue(property).replace('px', '');
}
export function setPxStyle(el, value, vertical) {
    value = vertical ? `0px, ${value}px, 0px` : `${value}px, 0px, 0px`;
    setTransform(el.style, `translate3d(${value})`);
}
export function getDataAttr(props) {
    return Object.keys(props).reduce((prev, key) => {
        if (key === 'role' || key.startsWith('aria-') || key.startsWith('data-')) {
            prev[key] = props[key];
        }
        return prev;
    }, {});
}
function toNum(style, property) {
    return +style.getPropertyValue(property).replace('px', '');
}
function getTypeValue(start, current, end, tabNode, wrapperNode) {
    let total = getStyle(wrapperNode, `padding-${start}`);
    const { parentNode } = tabNode;
    if (parentNode) {
        [...parentNode.childNodes].some((node) => {
            if (node !== tabNode) {
                // 此处对代码进行了修改 取自rc-tabs@9.4.2 这版本进行了计算方式的调整,避免了在类似modal等有动画的内容中使用的时候，计算出现错误的问题，因为在动画过程中的计算，会有一次Height width为0的情况
                // 在 9.4.2版本中 因为前几个版本的修改 refactor: rm mixin and react-create-class
                // 对dom结构进行了调整 bar不与item在一个父元素中,因此有如下代码，在c7n中暂时不进行dom结构调整
                if (node.className.includes('ink-bar')) {
                    return false;
                }
                const style = getComputedStyle(node);
                total += toNum(style, `margin-${start}`);
                total += toNum(style, `margin-${end}`);
                total += toNum(style, current);
                if (style.boxSizing === 'content-box') {
                    total += toNum(style, `border-${start}-width`) + toNum(style, `padding-${start}`) +
                        toNum(style, `border-${end}-width`) + toNum(style, `padding-${end}`);
                }
                return false;
            }
            return true;
        });
    }
    return total;
}
export function getLeft(tabNode, wrapperNode) {
    return getTypeValue('left', 'width', 'right', tabNode, wrapperNode);
}
export function getTop(tabNode, wrapperNode) {
    return getTypeValue('top', 'height', 'bottom', tabNode, wrapperNode);
}
export function getHeader(props) {
    const { tab, title } = props;
    if (typeof tab === 'function') {
        return tab(title);
    }
    if (title !== undefined) {
        return title;
    }
    if (tab !== undefined) {
        return tab;
    }
}
function sorter(item1, item2) {
    const { sort = 0 } = item1[1];
    const { sort: sort2 = 0 } = item2[1];
    return sort - sort2;
}
export function normalizePanes(children, customized, options) {
    const groups = toGroups(children);
    const groupedPanels = new Map();
    const panelList = [];
    const panes = customized && customized.panes;
    const omitKeys = [];
    if (options) {
        const { tabDraggable, tabTitleEditable, tabCountHideable } = options;
        if (!tabDraggable) {
            omitKeys.push('sort');
        }
        if (!tabTitleEditable) {
            omitKeys.push('title');
        }
        if (!tabCountHideable) {
            omitKeys.push('showCount');
        }
    }
    const { length } = omitKeys;
    const getCustomizedPane = (key) => {
        if (panes) {
            const pane = panes[key];
            if (pane && !pane.hidden) {
                if (length) {
                    return omit(pane, omitKeys);
                }
                return pane;
            }
        }
    };
    if (groups.length) {
        let index = 0;
        groups.forEach((group, i) => {
            if (!group.props.hidden) {
                const groupPanelList = [];
                toArray(group.props.children).forEach((child, j) => {
                    if (!child.props.hidden) {
                        const panelKey = generateKey(child.key, index);
                        index += 1;
                        groupPanelList.push([panelKey, { type: child.type, sort: j, ...child.props, ...getCustomizedPane(panelKey) }]);
                    }
                });
                groupPanelList.sort(sorter);
                panelList.push(...groupPanelList);
                const groupKey = generateKey(group.key, i);
                groupedPanels.set(groupKey, {
                    group: { ...group.props },
                    panelsMap: new Map(groupPanelList),
                });
            }
        });
    }
    else {
        toArray(children).forEach((child, index) => {
            if (!child.props.hidden) {
                const key = generateKey(child.key, index);
                panelList.push([key, { type: child.type, sort: index, ...child.props, ...getCustomizedPane(key) }]);
            }
        });
        panelList.sort(sorter);
    }
    return [new Map(panelList), groupedPanels];
}
